import { useCallback, useMemo, useRef, useState } from 'react';

import type { ModelProduct } from '@app/api/Products';
import type {
  AddAvailableProductsModalProps,
  ProductTreeLeaf,
} from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/types';
import { groupProductsByCategory } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/utils';
import type { RangeInterface } from '@app/shared/components/Range/types';

import type { TreeDataNode, TreeProps } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import type { ChangeEventHandler } from 'react';

export const useAddAvailableProductsModal = ({
  isOpen,
  onClose,
  productList,
  suppliersOptions,
  onAdd,
  selectedProducts,
  warehouseProducts,
}: AddAvailableProductsModalProps) => {
  const idToKeyRef = useRef<Map<number, React.Key>>(new Map());
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<BaseOptionType[]>(
    []
  );
  const [range, setRange] = useState<RangeInterface>({
    from: undefined,
    to: undefined,
  });
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  const onSelect: TreeProps['onSelect'] = (_, { node }) => {
    if (!node.isLeaf) return;
    const key = node.key as React.Key;
    setCheckedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const onCheck: TreeProps['onCheck'] = (chkKeys) =>
    setCheckedKeys(chkKeys as React.Key[]);

  const warehouseProductIdSet = useMemo(
    () => new Set(warehouseProducts.map((w) => w.ProductId)),
    [warehouseProducts]
  );

  const warehouseProductsMap = useMemo(() => {
    return new Map(warehouseProducts.map((w) => [w.ProductId, w]));
  }, [warehouseProducts]);

  const supplierSelectedSet = useMemo(() => {
    return new Set<number | null>(
      selectedSuppliers.map((o) => {
        if (o.value === null || o.value === undefined || o.value === '')
          return null;
        return Number(o.value);
      })
    );
  }, [selectedSuppliers]);

  const normalizedQuery = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return {
      raw: q,
      words: q ? q.split(/\s+/).filter(Boolean) : [],
    };
  }, [searchValue]);

  const rangeNorm = useMemo(() => {
    const fromRaw = range.from;
    const toRaw = range.to;
    const fromNum = fromRaw !== undefined ? Number(fromRaw) : null;
    const toNum = toRaw !== undefined ? Number(toRaw) : null;
    return { fromNum, toNum };
  }, [range.from, range.to]);

  const getTreeData = useCallback((): TreeDataNode[] => {
    idToKeyRef.current.clear();

    const { fromNum, toNum } = rangeNorm;
    const { raw: queryRaw, words: queryWords } = normalizedQuery;
    const hasSupplierFilter =
      supplierSelectedSet.size > 0 &&
      !(supplierSelectedSet.size === 1 && supplierSelectedSet.has(null));

    const filtered: ModelProduct[] = [];
    for (let i = 0; i < productList.length; i++) {
      const p = productList[i];
      const extraProduct = warehouseProductsMap.get(p.Id);

      if (!warehouseProductIdSet.has(p.Id)) continue;

      if (hasSupplierFilter) {
        const supplierId = extraProduct?.SupplierId || null;
        if (!supplierSelectedSet.has(supplierId)) {
          continue;
        }
      }

      if (fromNum !== null && Number(extraProduct?.PriceOfSale) <= fromNum)
        continue;
      if (toNum !== null && Number(extraProduct?.PriceOfSale) >= toNum)
        continue;

      if (queryRaw) {
        const f1 = p.Name?.toLowerCase();
        const f2 = p.NameShort?.toLowerCase();
        const f3 = p.Article?.toLowerCase();
        const f4 = p.BarCode?.toLowerCase();

        if (!(f1 || f2 || f3 || f4)) continue;

        let allWordsOk = true;
        outer: for (let w = 0; w < queryWords.length; w++) {
          const word = queryWords[w];
          if (
            (f1 && f1.includes(word)) ||
            (f2 && f2.includes(word)) ||
            (f3 && f3.includes(word)) ||
            (f4 && f4.includes(word))
          ) {
            continue outer;
          }
          allWordsOk = false;
          break;
        }
        if (!allWordsOk) continue;
      }

      filtered.push(p);
    }

    let groups = groupProductsByCategory(filtered, warehouseProductsMap);

    if (queryRaw) {
      const q = queryRaw;
      groups = groups.filter((g) => {
        const titleHit =
          typeof g?.title === 'string' && g.title.toLowerCase().includes(q);
        const hasKids = (g.children?.length ?? 0) > 0;
        return titleHit || hasKids;
      });
    }

    for (let c = 0; c < groups.length; c++) {
      const cat = groups[c];
      if (!cat.children) continue;
      for (let j = 0; j < cat.children.length; j++) {
        const leaf = cat.children[j] as ProductTreeLeaf;
        const product = leaf.dataRef;
        idToKeyRef.current.set(product.Id, leaf.key);
      }
    }

    return groups;
  }, [
    productList,
    warehouseProductIdSet,
    supplierSelectedSet,
    normalizedQuery,
    rangeNorm,
    idToKeyRef,
    warehouseProductsMap,
  ]);

  const treeData = useMemo(getTreeData, [getTreeData]);

  const checkedKeysHandler = useCallback(() => {
    if (isOpen) {
      const keys = selectedProducts
        .map((p) => idToKeyRef.current.get(p.Id))
        .filter((k): k is React.Key => k !== undefined);

      setCheckedKeys(keys);
    } else {
      setCheckedKeys([]);
    }
  }, [selectedProducts, isOpen]);

  const onSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchValue(e.target.value);
  };

  const skipFilters = useCallback(() => {
    setSearchValue('');
    setSelectedSuppliers([]);
    setRange({ from: undefined, to: undefined });
  }, []);

  const productMap = useMemo<Map<React.Key, ModelProduct>>(() => {
    const map = new Map<React.Key, ModelProduct>();

    treeData.forEach((cat) => {
      (cat.children ?? []).forEach((leaf) => {
        map.set(leaf.key, (leaf as ProductTreeLeaf).dataRef);
      });
    });

    return map;
  }, [treeData]);

  const addProducts = useCallback(() => {
    const selected: ModelProduct[] = checkedKeys
      .map((k) => productMap.get(k))
      .filter((p): p is ModelProduct => p !== undefined);

    onAdd(selected);
  }, [checkedKeys, onAdd, productMap]);

  const onCancel = useCallback(() => {
    setCheckedKeys([]);
    skipFilters();
    onClose();
  }, [onClose, skipFilters]);

  return {
    checkedKeys,
    onSelect,
    onCheck,
    isOpen,
    suppliersOptions,
    treeData,
    selectedSuppliers,
    setSelectedSuppliers,
    searchValue,
    onSearch,
    setRange,
    range,
    skipFilters,
    addProducts,
    onCancel,
    checkedKeysHandler,
  };
};
